import {
    splitReferenceData,
    alignRedundantReferenceMatchingPatterns,
    alignmentSizes,
    getMatchingPatterns,
    rulerPositions,
} from "./utils";

import {
    isInsideArea,
    linearDomain,
} from "../editor/utils";

import {
    MatchingPatternColors,
    Selector,
    zoomLevels,
} from "../mixins/dictionaries";

import Brush from "./linearBrush";

import {drag} from "d3-drag"
import {zoom as scroll} from "d3-zoom";
import {select, selectAll} from "d3-selection";
import {scaleLinear} from "d3-scale";
import {line, area} from "d3-shape";

const d3 = {select, selectAll, drag, scroll, line, area, scaleLinear};

export default class LinearPreview {
    constructor(settings, mediator) {
        this.mediator = mediator;

        this.EventsOn();

        this.initContainer();
        this.brush = new Brush();
    }

    initData(data) {
        this.positions = data.result.positions;

        this.reads = data.result.reads;

        this.referenceLength = data.result.reference.length;

        this.sequenceLength = this.referenceLength;
        this.maxChromatogramValue = 0;
        this.maxQualityValue = 0;

        this.tempStartOffsetAlignment = 0;
        this.tempEndOffestAlignment = 0;

        [this.sequenceLength, this.tempStartOffsetAlignment, this.tempEndOffestAlignment, this.maxChromatogramValue, this.maxQualityValue] = alignmentSizes(this.referenceLength, this.reads);

        this.domainX = linearDomain([0, this.sequenceLength], this.$previewBoxContainer.width);

        this.referenceMatchingPatterns = getMatchingPatterns(0, 0, this.referenceLength, 0, data.result.mismatches, data.result.deletions, data.result.insertions);

        this.readMatchingPatterns = [];
        this.readQualityValues = [];

        if (this.sequenceLength > this.referenceLength) {
            this.reference = splitReferenceData(this.tempStartOffsetAlignment, this.tempEndOffestAlignment, data.result.reference);

            this.positions = [
                ...data.result.positions.slice(this.referenceLength - this.tempStartOffsetAlignment, this.referenceLength),
                ...this.positions,
                ...data.result.positions.slice(0, this.tempEndOffestAlignment)
            ];
            this.referenceMatchingPatterns = alignRedundantReferenceMatchingPatterns(this.tempStartOffsetAlignment, this.tempEndOffestAlignment, this.referenceMatchingPatterns, this.referenceLength);
        } else {
            this.reference = splitReferenceData(0, this.referenceLength, data.result.reference);
        }

        this.ticks = rulerPositions(this.positions);

        this.reads.forEach(r => {
            if (r.successful) {
                this.readMatchingPatterns.push(getMatchingPatterns(r.start, r.start_trim, r.end, r.end_trim, r.mismatches, r.deletions, r.insertions, 0));
                this.readQualityValues.push(r.quality_values);
            } else {
                this.readMatchingPatterns.push([]);
                this.readQualityValues.push([]);
            }
        })

        this.initPosition = 0;
        this.offsetScroll = {
            "current": this.initPosition
        }

        this.brush.setData(this.previewBox, this.initPosition, this.$previewBoxContainer);
        this.brush.create();
        this.zoom = 12;
    }


    initContainer() {
        this.linearPreviewHeight = 120;
        this.$previewBox = document.querySelector(Selector.PREVIEW);
        this.$previewBox.style.height = this.linearPreviewHeight + "px";
        this.$previewBox.style.width = "100%";

        this.$previewBoxContainer = this.$previewBox.getBoundingClientRect();

        this.previewBox = d3.select(Selector.PREVIEW).append("svg")
            .style("height", this.$previewBoxContainer.height + "px")
            .style("width", this.$previewBoxContainer.width + "px")

        this.addEventsContainer();
    }

    setData(data) {
        this.initData(data);
        this.render();
    }

    render() {
        this.drawRuler();

        this.drawReference();
        this.drawMatchingPatternsReference();

        this.drawReads();
    }

    drawRuler() {
        const tickHeight = 4;
        const layer = 0;

        this.ticks.forEach(t => {
            this.drawTick(this.previewBox, layer, t, tickHeight);
        })

        if (this.sequenceLength > this.referenceLength) {
            const wrapStart = [
                {
                    text: 0,
                    position: this.tempStartOffsetAlignment
                },
                {
                    text: this.referenceLength,
                    position: this.tempStartOffsetAlignment + this.referenceLength
                },
            ]
            wrapStart.forEach((edge, index) => {
                this.previewBox
                    .append("line")
                    .attr("class", "wrap-start")
                    .attr("x1", this.domainX(edge.position))
                    .attr("y1", 0)
                    .attr("x2", this.domainX(edge.position))
                    .attr("y2", "100%")
                    .style("stroke-dasharray", ("3, 3"))

                this.drawTick(this.previewBox, layer, edge, tickHeight);
            })
        }
    }

    drawTick(container, layer, tick, height) {
        const positionTick = this.domainX(tick.position)
        const tickContainer = container.append("g")
            .attr("class", "tickGroup")
            .attr("transform", "translate( " + positionTick + "," + layer + ")");


        tickContainer.append("line")
            .attr("x1", 0)
            .attr("y1", height)
            .attr("x2", 1)
            .attr("y2", height)
            .style("stroke-width", "5")
            .style("opacity", "1")

        const tickNumberContainer = tickContainer
            .append("text")
            .attr("class", "tick")
            .attr("x", 0)
            .attr("dy", 15)
            .style("text-anchor", "middle")
            .text(tick.text);

        const tickContainerSize = tickContainer.node().getBBox().width;

        if (positionTick < (tickContainerSize / 2) ||
            positionTick > this.$previewBoxContainer.width - (tickContainerSize / 2)
        ) {
            tickNumberContainer.style("text-anchor", positionTick < (tickContainerSize / 2) ? "start" : "end")
        }
    }

    drawReference() {
        const offsetYReference = 30;
        const referenceHeight = 6;
        this.reference.forEach(r => {
            this.previewBox
                .append("line")
                .attr("class", "reference")
                .attr("x1", this.domainX(r.start))
                .attr("y1", offsetYReference)
                .attr("x2", this.domainX(r.end))
                .attr("y2", offsetYReference)
                .style("stroke-width", referenceHeight)
                .style("opacity", ".4")
        })
    }

    drawReads() {
        let offsetYReads = 44;
        const step = Math.min(10, (this.linearPreviewHeight - offsetYReads) / this.reads.length);
        const readHeight = Math.min(8, step - 2);
        const domainY = d3.scaleLinear()
            .range([readHeight, 0])
            .domain([0, this.maxQualityValue])

        this.reads.forEach((r, i) => {
            if (r.successful) {
                this.drawRead(i, r, domainY, readHeight, offsetYReads, this.tempStartOffsetAlignment)
                if (isInsideArea(this.referenceLength - this.tempStartOffsetAlignment, this.referenceLength, r.start - r.start_trim, r.end + r.end_trim, this.referenceLength)) {
                    this.drawRead(i, r, domainY, readHeight, offsetYReads, -this.referenceLength + this.tempStartOffsetAlignment)
                }
            }
            offsetYReads += step
        })
    }

    drawRead(index, r, domainY, height, offsetY, offsetX) {
        const startSlot = (r.start - r.start_trim);
        const container = this.previewBox
            .append("g")
            .attr("class", "read")
            .attr("transform", "translate(" + this.domainX(offsetX + startSlot) + ", " + offsetY + ")")

        this.drawQualityValues(container, this.readQualityValues[index], domainY, height);
        this.drawMatchingPatternsReads(container, this.readMatchingPatterns[index], offsetY, height, startSlot);
    }

    drawQualityValues(container, qualityValues, domainY, height) {
        if (qualityValues) {
            const path = qualityValues.map((qv, i) => {
                return [this.domainX(i), -height + domainY(qv)];
            })
            const area = d3.area();

            container
                .append("path")
                .attr("class", "basePairQualityValue")
                .attr("stroke-width", .1)
                .attr("opacity", ".5")
                .attr("d", area(path));
        }
    }

    drawMatchingPatternsReference() {
        const offsetYReference = 27;
        const referenceHeight = 6;
        this.referenceMatchingPatterns.forEach(mp => {
            this.drawMatchingPatternReference(this.previewBox, offsetYReference, referenceHeight, mp);
            this.drawMatchingPatternReferenceHighLight(this.previewBox, offsetYReference + 3, mp.start);
        })
    }

    drawMatchingPatternReference(container, positionY, height, mp) {
        container
            .append("rect")
            .attr("class", "matching-pattern")
            .attr("x", this.domainX(mp.start))
            .attr("y", positionY)
            .attr("height", height)
            .attr("width", this.domainX(mp.end - mp.start))
            .style("opacity", "1")
    }

    drawMatchingPatternReferenceHighLight(container, positionY, positionX) {
        container
            .append("circle")
            .attr("class", "matching-pattern")
            .attr("cx", this.domainX(positionX))
            .attr("cy", positionY)
            .attr("r", 8)
            .attr("fill", "none")
            .attr("stroke-width", ".5")
    }

    drawMatchingPatternsReads(container, matchingPatterns, layer, height, start) {
        matchingPatterns.forEach(mp => {
            container
                .append("rect")
                .attr("x", this.domainX(mp.start - start))
                .attr("y", mp.type !== "trim" ? -height : -(height / 2))
                .attr("height", mp.type !== "trim" ? height : ".5")
                .attr("width", this.domainX(mp.end - start) - this.domainX(mp.start - start))
                .style("fill", mp.type !== "trim" ? MatchingPatternColors[mp.type] : "#000")
                .style("opacity", "1")
        })
    }

    addEventsContainer() {
        this.previewBox
            .on("contextmenu", (e) => {
                e.preventDefault();
                e.stopPropagation();
            })
            .on("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                const minEdgeOffsetScroll = 0;
                const maxEdgeOffsetScroll = this.sequenceLength - this.domainX.invert(this.brushSize.width);
                this.offsetScroll.current = Math.round(this.domainX.invert(e.offsetX - (this.brushSize.width / 2)));

                if (this.offsetScroll.current < minEdgeOffsetScroll || this.offsetScroll.current > maxEdgeOffsetScroll) {
                    if (this.offsetScroll.current < minEdgeOffsetScroll) {
                        this.offsetScroll.current = minEdgeOffsetScroll;
                    } else {
                        this.offsetScroll.current = maxEdgeOffsetScroll;
                    }
                }

                this.EventsTrigger("setOffsetScroll", this.offsetScroll);
                this.EventsTrigger("slide", "end");

                this.slide();
            })
    }

    setBrushSize(data) {
        this.offsetScroll.current = data.position;

        this.brushSize = {
            x: this.domainX(this.offsetScroll.current),
            width: this.domainX(data.width)
        };

        this.brush.setParameters(this.brushSize);
    }

    setOffsetScroll(data) {
        this.offsetScroll.current = data.current;

        this.brush.setParameters({
            x: this.domainX(this.offsetScroll.current),
            width: this.brushSize.width
        })
    }

    setScroll(scroll) {
        if (this.zoom !== zoomLevels.max) {
            const min = 0
            const max = this.sequenceLength - this.domainX.invert(this.brushSize.width);

            this.offsetScroll.current -= scroll;

            if (this.offsetScroll.current < min || this.offsetScroll.current > max) {
                if (this.offsetScroll.current < min) {
                    this.offsetScroll.current = min
                } else {
                    this.offsetScroll.current = max
                }
            }
        }
    }

    slide() {
        this.brush.setParameters({
            x: this.domainX(this.offsetScroll.current),
            width: this.brushSize.width
        })
    }

    setFontWidth(data) {
        this.zoom = data;
    }

    EventsTrigger(action, data) {
        if (this.mediator) {
            this.mediator.trigger(action, data, this);
        }
    }

    EventsOn() {
        if (this.mediator) {
            this.mediator.on("setData", "setData", this);
            this.mediator.on("setFontWidth", "setFontWidth", this);
            this.mediator.on("setAreaView", "setBrushSize", this);
            this.mediator.on("setOffsetScroll", "setOffsetScroll", this);
            this.mediator.on("setScroll", "setScroll", this);
            this.mediator.on("slide", "slide", this);
        }
    }
}